import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Loading } from '@/components/loaders';
import { useSmsWorkspace } from '@/features/campaigns/v2/Sms/context/SmsWorkspaceContext';
import { FlowMappingsForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData, FlowMappingsConfig } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { hasMappingsUi } from '@/features/connectors/flows';
import { useSmsConnectorFlowRunner } from '@/features/connectors/sms';
import { RouteConstants } from '@/features/router/RouteConstants';
import { showToast } from '@/features/toasts/toastsSlice';
import { StepPageActions } from '@/global/components/Layout/StepPageActions';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers/CompanyProvider';
import { useAppDispatch } from '@/reducers';
import { Button } from '@/lib';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

type CreateTemplateOpts = {
  flowParams?: FlowFormData;
  flowMappings?: FlowFormData;
  fromFlowComplete?: boolean;
};

export const SmsTemplateInsertWorkaround = () => {
  const { connectorId } = useSmsWorkspace();
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const appDispatch = useAppDispatch();
  const { t } = useTranslation('campaign');
  const { t: tAll } = useTranslation('ALL');
  const history = useHistory();

  const {
    prepareResult,
    prepareError,
    isPreparePending,
    reloadPrepare,
    createInstance,
    isCreating,
    connectorFlowId,
    connectorInstanceId,
    isChannelLoading,
  } = useSmsConnectorFlowRunner('templateInsert', {
    audienceId,
    lang: currentLanguage,
    connectorId,
  });

  const [formData, setFormData] = useState<FlowFormData>({});
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);
  const [multiStepsCompleted, setMultiStepsCompleted] = useState(false);

  const handleCreateTemplate = async (opts?: CreateTemplateOpts) => {
    const flowParams = opts?.flowParams ?? formData;
    const flowMappings = opts?.flowMappings ?? mappingData;

    if (!opts?.fromFlowComplete && hasMappingsUi(multiStepMappingsConfig) && !isMappingsFormValid) {
      return { success: false, message: 'Invalid mappings' };
    }

    if (!connectorInstanceId) {
      const message = String(t('noConnectorInstanceFound'));
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    if (!audienceId) {
      const message = String(t('missingAudience.toast'));
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    const result = await createInstance(flowParams, flowMappings);
    if (!result.success) {
      const errorMessage = result.message ?? String(t('flowInstanceCreationFailed'));
      appDispatch(showToast({ type: ToastType.ERROR, title: errorMessage }));
      return { success: false, message: errorMessage };
    }

    appDispatch(showToast({ title: String(t('smsWorkspace.templates.createSuccess')), type: ToastType.SUCCESS }));
    history.push(RouteConstants.smsTemplates);
    return { success: true };
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel' as any,
    onClick: () => history.push(RouteConstants.smsTemplates),
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON.confirm' as any,
    onClick: () => void handleCreateTemplate(),
    disabled: !isMappingsFormValid || isCreating,
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const showMappingsStep = hasMappingsUi(multiStepMappingsConfig);
  const isTemplateLoading = isAudienceLoading || isChannelLoading || (hasAudience && isPreparePending);

  if (isAudienceLoading) {
    return (
      <div className='space-y-4'>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.templates.createTitle')}</h2>
        <Loading />
      </div>
    );
  }

  if (isAudienceSettled && !hasAudience) {
    return (
      <div className='space-y-4'>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.templates.createTitle')}</h2>
        <AudienceRequiredEmptyState />
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.templates.createTitle')}</h2>
      {!multiStepsCompleted && (
        <ErrorBoundary>
          {prepareResult ? (
            <MultiStepFlowForm
              key={`${connectorFlowId}-${audienceId}`}
              id='sms-template-insert-form'
              showTemplatePreview
              templatePreviewVariant='sms'
              audienceId={audienceId!}
              connectorInstanceId={connectorInstanceId!}
              connectorFlowId={connectorFlowId!}
              paramsConfig={prepareResult}
              onComplete={(allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => {
                setFormData(allParams);
                setMultiStepMappingsConfig(mappingsConfig);
                setMappingData({});
                setIsMappingsFormValid(false);
                if (hasMappingsUi(mappingsConfig)) {
                  setMultiStepsCompleted(true);
                } else {
                  void handleCreateTemplate({ flowParams: allParams, flowMappings: {}, fromFlowComplete: true });
                }
              }}
            />
          ) : (
            <div className='flex flex-col items-start gap-3'>
              <p className='text-sm text-gray-600'>
                {isTemplateLoading && !prepareError
                  ? t('smsWorkspace.templates.templateLoadingMessage')
                  : t('smsWorkspace.templates.failedToLoadTemplate')}
              </p>
              {prepareError ? <Button intent='secondary' onClick={() => reloadPrepare()} text={t('smsWorkspace.templates.tryAgain')} /> : null}
            </div>
          )}
        </ErrorBoundary>
      )}
      {multiStepsCompleted && showMappingsStep ? (
        <ErrorBoundary>
          <div>
            <h3 className='mb-4 text-lg font-medium'>{tAll('destinations.steps.configureFlow.dataMapping')}</h3>
            <FlowMappingsForm
              config={multiStepMappingsConfig}
              formData={mappingData}
              onFormDataChange={handleMappingDataChange}
              onValidationChange={handleMappingsValidationChange}
            />
            <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};
